import React, {memo, useCallback, useMemo, useState} from 'react';

import './index.scss';
import {PasswordContainer} from '../../../../components/PasswordContainer';
import {useSelector} from 'react-redux';
import {UserModel} from '../../../../types/control';
import {hasAuthority} from '../../../../utils/role';
import {SettingsPageContainer} from '../../../../components/Settings/SettingsPageContainer';
import {QRScreen} from '../../../QRScreen';
import {ColumnModel, SettingsList} from '../../../../components/Settings/SettingsList';
import {useRPC} from '../../../../hooks/rpc';
import {useMessage} from '../../../../hooks/message';

const roles = {
    'default': 'Varsayılan',
    'user': 'Kullanıcı',
    'admin': 'Yönetici',
    'service': 'Servis',
    'developer': 'Geliştirici',
};

const columns: ColumnModel[] = [
    {
        length: 3,
        name: 'Kullanıcılar',
        key: 'username',
    },
    {
        length: 1,
        name: 'Seviye',
        key: 'role',
    },
    {
        length: 1,
        name: 'OS',
        key: 'mobile_os',
    },
    {
        length: 2,
        name: 'Push Token',
        key: 'push_token',
    },
];

const keyExtractor = (item: UserModel) => item.id;
const classExtractor = (item: UserModel) => item.role;
const displayNameExtractor = (item: UserModel, column: ColumnModel) => {
    if (column.key === 'role') {
        return roles[item.role] || item.role || '-';
    }

    return item[column.key] || '-';
};
export const UserListScreen = memo(function UserListScreen() {
    const users = useSelector(state => state.control.userList);
    const role = useSelector(state => state.ui.role);
    const [rpc] = useRPC();
    const {success, failure} = useMessage();

    const [qrVisible, setQrVisible] = useState(false);

    const userList = useMemo(() => users.filter(user => hasAuthority(role, user.role)), [role, users]);
    const currentUsername = useSelector((state) => state.user.username);
    const currentUser = useMemo(() => userList.find((u) => u.username === currentUsername), [currentUsername, userList]);
    const [selectedUser, setSelectedUser] = useState<UserModel>(currentUser ? currentUser : null);

    const showQR = useCallback(() => {
        setQrVisible(true);
    }, []);

    const testNotification = useCallback(() => {
        rpc.testNotification(selectedUser.mobile_os, selectedUser.push_token)
            .then(() => success('Bildirim başarıyla gönderildi.'))
            .catch(failure);
    }, [rpc, success, failure, selectedUser]);

    const buttons = useMemo(() => [
        {
            text: 'DÜZENLE',
            url: `/settings/users/edit/${selectedUser ? selectedUser.id : ''}`,
            className: 'orange',
            hidden: !(selectedUser),
        },
        {
            text: 'BİLDİRİM TESTİ',
            className: 'red',
            hidden: !(selectedUser) || !(selectedUser?.push_token),
            onClick: testNotification,
        },
        {
            text: 'QR OLUŞTUR',
            onClick: showQR,
            className: 'blue',
            hidden: !(selectedUser && selectedUser.role !== 'service'),
        },
        {
            text: 'KULLANICI OLUŞTUR',
            url: '/settings/users/edit',
            className: 'green',
            hidden: !hasAuthority(role, 'admin'),
        },
    ], [role, selectedUser, showQR, testNotification]);

    return (
        <>
            <PasswordContainer requiredRole='user' isFullscreen={true}>
                <SettingsPageContainer
                    buttons={buttons}
                    className='user-list-page'
                    backUrl={'/settings'}
                    title='Kullanıcılar'>
                    <SettingsList
                        columns={columns}
                        items={userList}
                        keyExtractor={keyExtractor}
                        classExtractor={classExtractor}
                        displayNameExtractor={displayNameExtractor}
                        selectedItem={selectedUser}
                        onItemSelected={setSelectedUser}
                        notSelectableItems={[{
                            id: 'default',
                            username: 'Varsayılan Kullanıcı',
                            role: 'default',
                            mobile_os: '-',
                            push_token: '-',
                        }]}/>
                </SettingsPageContainer>
            </PasswordContainer>
            {
                renderIf(qrVisible)(
                    () => <QRScreen id={selectedUser.id} onClick={() => setQrVisible(false)}/>,
                )
            }
        </>
    );
});
